import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ProfileContent from "./ProfileContent";
import BusinessDetailsForm from "./forms/BusinessDetailsForm";
import LocationSelectionForm from "./forms/LocationSelectionForm";

interface BusinessProfileFormProps {
  isEditing: boolean;
  categories: string[];
  address: string;
  businessName: string;
  businessPhone: string;
  businessWebsite: string;
  businessDescription: string;
  onCategoriesChange: (value: string[]) => void;
  onLocationSelect: (location: { 
    lat: number; 
    lng: number; 
    formattedAddress: string;
    placeId?: string;
    businessDetails?: {
      name?: string;
      phoneNumber?: string;
      website?: string;
      rating?: number;
      userRatingsTotal?: number;
      reviews?: google.maps.places.PlaceReview[];
      photoUrl?: string;
    };
  }) => void;
  onBusinessNameChange: (value: string) => void;
  onBusinessPhoneChange: (value: string) => void;
  onBusinessWebsiteChange: (value: string) => void;
  onBusinessDescriptionChange: (value: string) => void;
}

const BusinessProfileForm = ({
  isEditing,
  categories,
  address,
  businessName,
  businessPhone,
  businessWebsite,
  businessDescription,
  onCategoriesChange,
  onLocationSelect,
  onBusinessNameChange,
  onBusinessPhoneChange,
  onBusinessWebsiteChange,
  onBusinessDescriptionChange,
}: BusinessProfileFormProps) => {
  const [status, setStatus] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [links, setLinks] = useState<{ url: string; title: string }[]>([]);
  const [newLink, setNewLink] = useState({ url: "", title: "" });
  const { toast } = useToast();

  const sponsors = [
    { name: "Sponsor 1", logo: "/sponsor1.png" },
    { name: "Sponsor 2", logo: "/sponsor2.png" }
  ];

  const handleLocationSelect = async (location: Parameters<typeof onLocationSelect>[0]) => {
    onLocationSelect(location);
    
    if (location.businessDetails) {
      onBusinessNameChange(location.businessDetails.name || businessName);
      onBusinessPhoneChange(location.businessDetails.phoneNumber || businessPhone);
      onBusinessWebsiteChange(location.businessDetails.website || businessWebsite);
    }
  };

  const addLink = (link: { url: string; title: string }) => {
    if (link.url && link.title) {
      setLinks([...links, link]);
      setNewLink({ url: "", title: "" });
    }
  };

  return (
    <div className="w-full md:w-2/3 relative space-y-6">
      <LocationSelectionForm
        isEditing={isEditing}
        address={address}
        onLocationSelect={handleLocationSelect}
      />

      <BusinessDetailsForm
        isEditing={isEditing}
        businessName={businessName}
        businessPhone={businessPhone}
        businessWebsite={businessWebsite}
        businessDescription={businessDescription}
        categories={categories}
        onBusinessNameChange={onBusinessNameChange}
        onBusinessPhoneChange={onBusinessPhoneChange}
        onBusinessWebsiteChange={onBusinessWebsiteChange}
        onBusinessDescriptionChange={onBusinessDescriptionChange}
        onCategoriesChange={onCategoriesChange}
      />

      <ProfileContent
        isEditing={isEditing}
        status={status}
        categories={categories}
        videoUrl={videoUrl}
        links={links}
        sponsors={sponsors}
        onStatusChange={setStatus}
        onCategoriesChange={onCategoriesChange}
        onVideoUrlChange={setVideoUrl}
        onAddLink={addLink}
        newLink={newLink}
        onNewLinkChange={setNewLink}
      />
    </div>
  );
};

export default BusinessProfileForm;